import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UsersRound } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  ListToolbar,
  QueryState,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  notify,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { PartyPicker } from '../components/party-picker.tsx'
import {
  ApiError,
  fetchChannelAccounts,
  fetchChannelGroups,
  followChannelGroup,
  setChannelGroupParty,
  unfollowChannelGroup,
  type ChannelGroupView,
} from '../lib/api.ts'

/**
 * Os grupos dos lojistas (F268).
 *
 * Lojas com mais de um funcionário falam com a loja no GRUPO do WhatsApp, e
 * até aqui essas mensagens nem chegavam ao Atendimento. O gerente escolhe,
 * número por número, quais grupos acompanhar e de qual loja cliente é cada um;
 * a conversa do grupo cai para o vendedor dessa loja.
 *
 * Só o grupo acompanhado entra — o grupo da família da atendente, não.
 */

const errorText = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

/** A linha em edição: escolhendo a loja antes de acompanhar ou ao trocar. */
type Editing = { groupJid: string; partyId: string }

export const AtendimentoGruposPage = (): ReactNode => {
  const queryClient = useQueryClient()
  const [chosenAccount, setChosenAccount] = useState('')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Editing | null>(null)

  const accounts = useQuery({
    queryKey: ['communication-channel-accounts'],
    queryFn: async ({ signal }) => await fetchChannelAccounts(signal),
  })
  const devices = (accounts.data ?? []).filter(
    (account) => account.connection === 'linked_device'
  )
  const accountId =
    chosenAccount === '' ? (devices[0]?.id ?? '') : chosenAccount

  const groupsKey = ['communication-channel-groups', accountId]
  const groups = useQuery({
    queryKey: groupsKey,
    queryFn: async ({ signal }) => await fetchChannelGroups(accountId, signal),
    enabled: accountId !== '',
  })

  const refresh = async (): Promise<void> => {
    setEditing(null)
    await queryClient.invalidateQueries({ queryKey: groupsKey })
    await queryClient.invalidateQueries({
      queryKey: ['communication-conversations'],
    })
  }

  const save = useMutation({
    mutationFn: async (input: { group: ChannelGroupView; partyId: string }) => {
      const partyId = input.partyId === '' ? null : input.partyId
      return input.group.followed && input.group.contactId !== null
        ? await setChannelGroupParty(input.group.contactId, partyId)
        : await followChannelGroup(accountId, {
            groupJid: input.group.groupJid,
            name: input.group.name,
            partyId,
          })
    },
    onSuccess: async (group) => {
      notify.success(
        group.partyName === null
          ? `${group.name}: acompanhado, sem loja ligada.`
          : `${group.name}: acompanhado, da loja ${group.partyName}.`
      )
      await refresh()
    },
    onError: (cause) => {
      notify.error(errorText(cause, 'Não foi possível salvar o grupo.'))
    },
  })

  const stop = useMutation({
    mutationFn: async (contactId: string) =>
      await unfollowChannelGroup(contactId),
    onSuccess: async (group) => {
      notify.success(
        `${group.name} deixou de entrar no Atendimento. A conversa já gravada fica.`
      )
      await refresh()
    },
    onError: (cause) => {
      notify.error(errorText(cause, 'Não foi possível parar de acompanhar.'))
    },
  })

  const term = search.trim().toLocaleLowerCase('pt-BR')
  const rows = (groups.data ?? []).filter(
    (group) =>
      term === '' ||
      group.name.toLocaleLowerCase('pt-BR').includes(term) ||
      (group.partyName ?? '').toLocaleLowerCase('pt-BR').includes(term)
  )

  return (
    <>
      <TabHeader
        title="Grupos do WhatsApp"
        description="Escolha os grupos dos clientes que entram no Atendimento e de qual loja é cada um. A conversa do grupo cai para o vendedor da loja. Grupo não acompanhado continua fora, sem nada gravado."
      />

      {accounts.isSuccess && devices.length === 0 ? (
        <EmptyState
          icon={<UsersRound aria-hidden="true" className="h-8 w-8" />}
          title="Nenhum número ligado pelo aparelho"
          description="Grupo só existe nos números da loja ligados pelo celular (WuzAPI). O número oficial não participa de grupo."
        />
      ) : (
        <>
          <ListToolbar>
            <SearchInput
              aria-label="Buscar grupo"
              placeholder="Buscar pelo grupo ou pela loja"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
            />
            {devices.length > 1 ? (
              <Select
                aria-label="Número da loja"
                value={accountId}
                onChange={(event) => {
                  setChosenAccount(event.target.value)
                  setEditing(null)
                }}
                className="w-56"
              >
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.displayName}
                  </option>
                ))}
              </Select>
            ) : null}
          </ListToolbar>

          <QueryState
            isPending={accounts.isPending || groups.isPending}
            isError={accounts.isError || groups.isError}
            error={groups.error ?? accounts.error}
            onRetry={() => {
              void groups.refetch()
            }}
            what="os grupos do número"
            isEmpty={rows.length === 0}
            filtered={term !== ''}
            onClearFilters={() => {
              setSearch('')
            }}
            empty={
              <EmptyState
                icon={<UsersRound aria-hidden="true" className="h-8 w-8" />}
                title="Este número não está em nenhum grupo"
                description="Quando o número da loja entrar no grupo de um cliente, ele aparece aqui."
              />
            }
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Grupo</TableHeaderCell>
                    <TableHeaderCell>Pessoas</TableHeaderCell>
                    <TableHeaderCell>Loja</TableHeaderCell>
                    <TableHeaderCell>
                      <span className="sr-only">Ações</span>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((group) => (
                    <GroupRow
                      key={group.groupJid}
                      group={group}
                      editing={
                        editing?.groupJid === group.groupJid ? editing : null
                      }
                      busy={save.isPending || stop.isPending}
                      onEdit={(partyId) => {
                        setEditing({ groupJid: group.groupJid, partyId })
                      }}
                      onCancel={() => {
                        setEditing(null)
                      }}
                      onSave={(partyId) => {
                        save.mutate({ group, partyId })
                      }}
                      onStop={() => {
                        if (group.contactId !== null) {
                          stop.mutate(group.contactId)
                        }
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </QueryState>
          {groups.data?.some((group) => group.participants === null) ===
          true ? (
            <InlineAlert tone="info">
              Os grupos com “—” em Pessoas são acompanhados, mas o número da
              loja não está mais neles. Pare de acompanhar se a loja saiu de
              vez.
            </InlineAlert>
          ) : null}
        </>
      )}
    </>
  )
}

const GroupRow = ({
  group,
  editing,
  busy,
  onEdit,
  onCancel,
  onSave,
  onStop,
}: {
  group: ChannelGroupView
  editing: Editing | null
  busy: boolean
  onEdit: (partyId: string) => void
  onCancel: () => void
  onSave: (partyId: string) => void
  onStop: () => void
}): ReactNode => (
  <TableRow>
    <TableCell>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-(--weight-medium)">{group.name}</span>
        {group.followed ? <Badge tone="success">Acompanhado</Badge> : null}
      </div>
    </TableCell>
    <TableCell className="tabular-nums">{group.participants ?? '—'}</TableCell>
    <TableCell>
      {editing === null ? (
        group.followed ? (
          (group.partyName ?? (
            <span className="text-(--text-secondary)">Sem loja</span>
          ))
        ) : (
          <span className="text-(--text-secondary)">—</span>
        )
      ) : (
        <PartyPicker
          value={editing.partyId}
          onChange={onEdit}
          role="customer"
          placeholder="Loja do cliente (opcional)"
          className="min-w-64"
        />
      )}
    </TableCell>
    <TableCell>
      <div className="flex justify-end gap-2">
        {editing !== null ? (
          <>
            <Button variant="ghost" onClick={onCancel} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onSave(editing.partyId)
              }}
              disabled={busy}
            >
              {group.followed ? 'Salvar' : 'Acompanhar'}
            </Button>
          </>
        ) : group.followed ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                onEdit(group.partyId ?? '')
              }}
              disabled={busy}
            >
              Trocar loja
            </Button>
            <Button variant="ghost" onClick={onStop} disabled={busy}>
              Parar
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              onEdit('')
            }}
            disabled={busy}
          >
            Acompanhar
          </Button>
        )}
      </div>
    </TableCell>
  </TableRow>
)
